import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { setSeoMetadata } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Industry landing pages.
 *
 * These used to sell autonomous cleaning robots per vertical. Converted to
 * sell what the business actually offers now: custom software, websites and
 * SEO, framed per industry. The route (/industries/:slug), the nav entries
 * and the data-driven page shape are unchanged - only the content is.
 *
 * `services` on each entry are labels from the same list used on the
 * Schedule a Consultation form and the homepage estimator (see
 * ProjectEstimatorSection.jsx / ScheduleDemoPage.jsx), not invented per page,
 * so the site describes its offering the same way everywhere.
 */

// One blurb per service, reused across every industry below. The "Build"
// sublabels are copied verbatim from ProjectEstimatorSection.jsx's
// PROJECT_TYPES so the two lists never drift apart.
const SERVICE_INFO = {
  'Web Application': { blurb: 'Dashboards, portals, tools' },
  'CRM / Internal System': { blurb: 'Pipelines, records, reporting' },
  'E-commerce': { blurb: 'Catalog, checkout, fulfilment' },
  'Mobile App': { blurb: 'iOS and Android' },
  'AI / Automation': { blurb: 'Assistants, document processing' },
  'Website Redesign': { blurb: 'A faster site on your existing brand' },
  'SEO & Content': { blurb: 'Get found by people already searching for you' },
  'Hosting & Maintenance': { blurb: 'Reliable hosting and support after launch' },
  'Integrations': { blurb: 'Connect the tools you already use' },
  'Take Over Existing Project': { blurb: 'Pick up a codebase and keep building' },
};

const INDUSTRIES = {
  'retail-uses': {
    name: 'Retail',
    title: 'Custom Software & SEO for Retail Businesses',
    description: 'From online storefronts to inventory systems, we build the software retail businesses need to compete online - plus the SEO to get found while they wait.',
    heroImage: '/images/home/hero-data-center.jpg',
    benefits: [
      'Online storefronts that convert browsers into buyers',
      'Inventory systems that stay in sync across channels',
      'Local SEO to win "near me" searches',
      'Mobile-friendly shopping from first click to checkout',
      'Customer data you own, not a platform',
      'Faster pages, fewer abandoned carts',
    ],
    challenges: [
      { problem: 'Customers can’t find you online', solution: 'Local SEO and content built around how people actually search' },
      { problem: 'Inventory out of sync across channels', solution: 'A system that keeps storefront, POS and warehouse stock aligned' },
      { problem: 'A slow, dated website', solution: 'A rebuild optimized for speed and conversions, not just looks' },
    ],
    services: ['E-commerce', 'SEO & Content', 'Website Redesign', 'Integrations'],
  },
  'warehouses': {
    name: 'Warehouses & Logistics',
    title: 'Operations Software for Warehouses & Logistics',
    description: 'Internal systems, inventory tracking and automation that keep operations running without the spreadsheet chaos.',
    heroImage: '/images/home/hero-data-center.jpg',
    benefits: [
      'Real-time inventory visibility',
      'Custom dashboards for floor operations',
      'Automate manual data entry and reporting',
      'Connect your WMS, ERP and shipping tools',
      'Fewer errors, faster fulfilment',
      'Built around how your team actually works',
    ],
    challenges: [
      { problem: 'Inventory tracked across disconnected spreadsheets', solution: 'One internal system built around your actual workflow' },
      { problem: 'Manual reporting eats staff hours', solution: 'Automated reporting and operations dashboards' },
      { problem: 'Legacy tools that don’t talk to each other', solution: 'Integrations that connect what you already run' },
    ],
    services: ['CRM / Internal System', 'AI / Automation', 'Integrations', 'Hosting & Maintenance'],
  },
  'hospitality': {
    name: 'Hospitality',
    title: 'Booking Systems & SEO for Hotels & Hospitality',
    description: 'Direct booking software, guest management and SEO that gets your property found before a third-party site takes the commission.',
    heroImage: '/images/home/hero-data-center.jpg',
    benefits: [
      'A direct booking engine that skips the OTA commission',
      'Guest history and preferences in one place',
      'Local SEO for "hotels near me" searches',
      'A booking flow that works on a phone',
      'Integration with the PMS you already run',
      'A website that actually matches your brand',
    ],
    challenges: [
      { problem: 'Losing margin to third-party booking sites', solution: 'A direct booking system that keeps more of every reservation' },
      { problem: 'A dated website hurting first impressions', solution: 'A redesign built around conversions and your brand' },
      { problem: 'Guest data scattered across tools', solution: 'A CRM that keeps guest history in one place' },
    ],
    services: ['Website Redesign', 'CRM / Internal System', 'SEO & Content', 'Integrations'],
  },
  'events-stadiums': {
    name: 'Events & Stadiums',
    title: 'Ticketing & Event Management Software',
    description: 'Custom ticketing, attendee management and event pages built for venues that have outgrown an off-the-shelf platform.',
    heroImage: '/images/home/hero-data-center.jpg',
    benefits: [
      'Ticketing without a per-ticket platform fee',
      'Real-time attendee and capacity tracking',
      'Integration with your existing POS and access control',
      'Mobile check-in for faster entry',
      'Event pages built to rank in search',
      'Attendee data you own outright',
    ],
    challenges: [
      { problem: 'Per-ticket fees eating into revenue', solution: 'A custom ticketing system you own outright' },
      { problem: 'Disconnected check-in and access control', solution: 'Systems integrated around how your venue actually runs' },
      { problem: 'Event pages that don’t rank', solution: 'SEO and content built for each event' },
    ],
    services: ['Web Application', 'Integrations', 'AI / Automation', 'SEO & Content'],
  },
  'education': {
    name: 'Education',
    title: 'Software & SEO for Schools & Universities',
    description: 'Portals, scheduling systems and websites built for the realities of education budgets and accessibility requirements.',
    heroImage: '/images/home/hero-data-center.jpg',
    benefits: [
      'Parent and student portals',
      'Scheduling and enrollment systems',
      'Accessible site design for every visitor',
      'SEO to reach prospective students and families',
      'Integration with the SIS or LMS you already use',
      'Scoped to fit education budgets',
    ],
    challenges: [
      { problem: 'A dated or inaccessible website', solution: 'An accessibility-minded redesign built for every visitor' },
      { problem: 'Manual enrollment and scheduling', solution: 'A portal that automates the busywork' },
      { problem: 'Limited budget for custom software', solution: 'Phased scoping that fits education budgets' },
    ],
    services: ['Web Application', 'CRM / Internal System', 'SEO & Content', 'Hosting & Maintenance'],
  },
  'healthcare': {
    name: 'Healthcare',
    title: 'HIPAA-Minded Software for Healthcare Practices',
    description: 'Patient portals, scheduling systems and SEO built for healthcare providers - with privacy and compliance as the starting point, not an afterthought.',
    metaDescription: 'Custom software and SEO for healthcare practices and facilities - patient portals, scheduling systems, and HIPAA-minded development.',
    metaKeywords: 'healthcare software development, medical practice SEO, patient portal development, healthcare website design, HIPAA minded software',
    heroImage: '/images/home/hero-data-center.jpg',
    benefits: [
      'Patient portals built with privacy in mind',
      'Scheduling and intake systems that reduce no-shows',
      'SEO to reach patients searching for care',
      'Integration with your existing EHR where possible',
      'Accessible design for every patient',
      'Reporting dashboards for practice operations',
      'Mobile-friendly for patients and staff alike',
      'Compliance considered from day one, not bolted on after',
    ],
    challenges: [
      { problem: 'Patient data requires careful handling', solution: 'Development built around HIPAA-minded data handling from the start' },
      { problem: 'Patients can’t find your practice online', solution: 'Local SEO and content built around how patients actually search' },
      { problem: 'Manual scheduling and intake', solution: 'A portal that automates scheduling and cuts down on no-shows' },
      { problem: 'Legacy systems that don’t integrate', solution: 'Integrations that connect with what you already run, where possible' },
      { problem: 'Reporting takes hours of staff time', solution: 'Automated dashboards for day-to-day practice operations' },
      { problem: 'Limited budget for custom development', solution: 'Phased builds scoped to fit practice budgets' },
    ],
    useCases: [
      { area: 'Patient Portals', description: 'Secure scheduling, intake forms and communication built around your workflow' },
      { area: 'Practice Websites', description: 'SEO-driven sites that bring in new patients searching for care' },
      { area: 'Scheduling Systems', description: 'Fewer no-shows with automated reminders and easy rebooking' },
      { area: 'Reporting Dashboards', description: 'Practice operations data in one place instead of scattered spreadsheets' },
      { area: 'Telehealth Integration', description: 'Connect virtual care tools into your existing workflow' },
      { area: 'Multi-location Practices', description: 'Centralized systems that work the same way across every location' },
    ],
    stats: [
      { value: 'HIPAA-minded', label: 'Development Approach' },
      { value: '24/7', label: 'Hosting & Uptime Monitoring' },
      { value: 'Phased', label: 'Scoped, Fixed-Price Delivery' },
      { value: '100%', label: 'Client-Owned Code & Data' },
    ],
    services: ['Web Application', 'CRM / Internal System', 'SEO & Content', 'Integrations'],
    testimonial: {
      quote: 'They scoped the patient portal in writing before any code was written, so we knew exactly what we were getting and what it would cost. No surprises at delivery.',
      author: 'Practice Manager',
      facility: 'Multi-location Medical Practice',
    },
  },
};

const IndustryPage = () => {
  const { industrySlug } = useParams();
  const industry = INDUSTRIES[industrySlug];
  const isHealthcare = industrySlug === 'healthcare';

  useEffect(() => {
    if (industry) {
      // setSeoMetadata appends the configured business name to the title
      // itself, so the title here carries no brand literal.
      setSeoMetadata({
        title: industry.title,
        description: industry.metaDescription || industry.description,
        keywords: industry.metaKeywords || `software development ${industry.name.toLowerCase()}, SEO ${industry.name.toLowerCase()}, custom software ${industry.name.toLowerCase()}`,
        canonicalPath: `/industries/${industrySlug}`,
      });
    }
  }, [industry, industrySlug]);

  if (!industry) {
    return (
      <div className="min-h-screen bg-bots-dark flex items-center justify-center">
        <Header />
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Industry Not Found</h1>
          <Link to="/" className="text-blue-400 hover:underline">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bots-dark">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${industry.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bots-dark via-bots-dark/90 to-bots-dark" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <p className="text-blue-400 font-semibold mb-4 uppercase tracking-wider">{industry.name} Industry</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">{industry.title}</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">{industry.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/schedule-a-demo"
              className="inline-block px-8 py-4 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500 transition-colors"
              data-testid="industry-cta-demo"
            >
              Schedule a Consultation
            </Link>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-bots-dark transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section - Healthcare Only */}
      {isHealthcare && industry.stats && (
        <section className="py-12 bg-bots-surface border-y border-gray-800">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {industry.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-green-400 mb-2">{stat.value}</p>
                  <p className="text-gray-400 text-sm md:text-base">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="py-20 bg-bots-surface">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Benefits for {industry.name}
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            {isHealthcare
              ? 'Software built specifically for the privacy and compliance demands of healthcare environments.'
              : `How custom software and SEO change the way ${industry.name.toLowerCase()} businesses operate online.`
            }
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {industry.benefits.map((benefit, index) => (
              <div
                key={benefit}
                className="flex items-start p-6 bg-bots-dark rounded-xl border border-gray-800 hover:border-green-500/30 transition-colors"
              >
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-0.5" />
                <p className="text-white text-sm md:text-base">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section - Healthcare Only */}
      {isHealthcare && industry.useCases && (
        <section className="py-20 bg-bots-dark">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              Where This Helps
            </h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              Built across the parts of a practice that create the most day-to-day friction.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="p-6 bg-bots-surface rounded-xl border border-gray-800 hover:border-blue-500/30 transition-colors"
                >
                  <h3 className="text-lg font-bold text-blue-400 mb-2">{useCase.area}</h3>
                  <p className="text-gray-300 text-sm">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Challenges & Solutions */}
      <section className={`py-20 ${isHealthcare ? 'bg-bots-surface' : 'bg-bots-dark'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            How We Solve {industry.name} Challenges
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            {`The problems that come up most often for ${industry.name.toLowerCase()} businesses, and how we approach each one.`}
          </p>
          <div className="space-y-6">
            {industry.challenges.map((item, index) => (
              <div
                key={index}
                className="bg-bots-dark p-6 rounded-xl border border-gray-800 grid md:grid-cols-2 gap-6"
              >
                <div>
                  <p className="text-red-400 font-semibold mb-2 text-sm uppercase tracking-wider">Challenge:</p>
                  <p className="text-white">{item.problem}</p>
                </div>
                <div>
                  <p className="text-green-400 font-semibold mb-2 text-sm uppercase tracking-wider">Our Approach:</p>
                  <p className="text-white">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section - Healthcare Only */}
      {isHealthcare && industry.testimonial && (
        <section className="py-20 bg-bots-dark">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <blockquote className="text-2xl md:text-3xl text-white font-light italic mb-8">
              "{industry.testimonial.quote}"
            </blockquote>
            <div>
              <p className="text-blue-400 font-semibold">{industry.testimonial.author}</p>
              <p className="text-gray-400">{industry.testimonial.facility}</p>
            </div>
          </div>
        </section>
      )}

      {/* Services for this industry */}
      <section className="py-20 bg-bots-surface">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Services for {industry.name}
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            {`The work we most often do for ${industry.name.toLowerCase()} clients. Every project starts with a written scope before any code.`}
          </p>
          <div className={`grid ${isHealthcare ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'} gap-8 ${isHealthcare ? '' : 'max-w-4xl mx-auto'}`}>
            {industry.services.map((serviceLabel) => {
              const info = SERVICE_INFO[serviceLabel];
              if (!info) return null;
              return (
                <Link
                  key={serviceLabel}
                  to="/contact"
                  className="bg-bots-dark p-8 rounded-2xl border border-gray-800 hover:border-blue-500/50 transition-colors group text-center"
                  data-testid={`industry-service-${serviceLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <h3 className="text-lg md:text-xl font-bold text-white mb-2">{serviceLabel}</h3>
                  <p className="text-gray-400 text-sm mb-4">{info.blurb}</p>
                  <span className="inline-flex items-center text-blue-400 group-hover:text-blue-300 text-sm">
                    Ask About This <ArrowRight className="w-4 h-4 ml-2" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {isHealthcare
              ? 'Ready for Software Built Around Patient Privacy?'
              : `Ready to Modernize Your ${industry.name} Operations?`
            }
          </h2>
          <p className="text-white/80 text-lg mb-8">
            {`Tell us what you're trying to build or fix, and we'll come back with a written scope, a cost range and a timeline.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/schedule-a-demo"
              className="px-8 py-4 bg-white text-blue-600 font-bold rounded-full hover:bg-gray-100 transition-colors"
            >
              Schedule a Consultation
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-[#FF6B00] text-black font-bold rounded-full hover:bg-[#ff8124] transition-colors"
            >
              Get Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustryPage;
